import { Capacitor } from "@capacitor/core";
import { isAxiosError } from "axios";
import type { Token } from "../contexts/AuthProvider";
import { backendService } from "../services/backend.service";
import { readRefreshToken } from "../services/storageService.service";
import { trackEvent } from "./analytics";
import type { UserView } from "./models";

/** Waits before each retry of a mint that failed without a server answer, or with a 5xx. */
export const DEPARTMENT_TOKEN_RETRY_DELAYS_MS = [1_000, 3_000];

/**
 * Mints a token for `departmentId` from the refresh token (web: the http-only cookie; native: the stored
 * one). `view` is passed by the caller rather than read from localStorage, which another tab may have
 * changed. The server re-validates it and can still refuse.
 */
export const requestDepartmentToken = async (departmentId: string, view: UserView): Promise<Token> => {
	if (!Capacitor.isNativePlatform()) {
		return (await backendService.getDepartmentToken(departmentId, view)).data;
	}
	const { value: refreshToken, readFailed } = await readRefreshToken();
	if (!refreshToken) {
		const reason = readFailed ? "read_failed" : "absent";
		trackEvent("department_token_request_aborted", { reason });
		throw new Error(`department token not requested: the stored refresh token is ${reason}`);
	}
	return (await backendService.getDepartmentToken(departmentId, view, refreshToken)).data;
};

/** A refusal (4xx) will be refused again; anything without a server answer, or a 5xx, may not be. */
const isRetryableTokenError = (error: unknown): boolean => {
	if (!isAxiosError(error)) return false;
	const status = error.response?.status;
	return status === undefined || status >= 500;
};

const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/** `requestDepartmentToken`, retried through a network blip or a deploy's 502s. */
export const requestDepartmentTokenWithRetry = async (departmentId: string, view: UserView): Promise<Token> => {
	for (const delayMs of DEPARTMENT_TOKEN_RETRY_DELAYS_MS) {
		try {
			return await requestDepartmentToken(departmentId, view);
		} catch (error) {
			if (!isRetryableTokenError(error)) throw error;
			trackEvent("department_token_retry", { department_id: departmentId, delay_ms: delayMs });
			await wait(delayMs);
		}
	}
	return requestDepartmentToken(departmentId, view);
};
